import { boolean, catch as zodCatch, enum as zodEnum, minLength, string } from 'zod/mini';
import {
    DASHBOARD_AGGREGATES,
    DASHBOARD_DEFAULT_AGGREGATE,
    DASHBOARD_NO_COLUMN,
} from '../data/dashboard-aggregate';
import { describeField } from './dashboard-widget-schema';
import type { DashboardWidgetSettingsContext } from './dashboard-widget-registry';

/** A single line of text the author types. */
export function textField(label: string) {
    return describeField(string(), { label });
}

/** A switch the author turns on or off. Leave `fallbackValue` out to fall back to off. */
export function switchField(label: string, fallbackValue?: boolean) {
    const flag = boolean();
    return describeField(fallbackValue === undefined ? flag : zodCatch(flag, fallbackValue), {
        label,
    });
}

/** A dropdown of a fixed set of values. Leave `fallbackValue` out to fall back to the first one. */
export function choiceField<T extends readonly string[]>(
    values: T,
    label: string,
    fallbackValue?: T[number]
) {
    const choices = zodEnum(values);
    return describeField(fallbackValue === undefined ? choices : zodCatch(choices, fallbackValue), {
        label,
    });
}

/** A dropdown of column keys from the bound query */
export function columnField(
    label: string,
    columns: (context: DashboardWidgetSettingsContext) => readonly string[]
) {
    return describeField(zodCatch(string().check(minLength(1)), DASHBOARD_NO_COLUMN), {
        label,
        choices: (context) => [DASHBOARD_NO_COLUMN, ...columns(context)],
    });
}

/** The fields every measure has. */
export function measureFields(columnLabel: string, aggregateLabel: string, whereLabel: string) {
    return {
        column: columnField(columnLabel, (context) => context.columns.map((column) => column.key)),
        aggregate: choiceField(DASHBOARD_AGGREGATES, aggregateLabel, DASHBOARD_DEFAULT_AGGREGATE),
        // Only a boolean column can be a condition, so a query with none offers `(none)` alone.
        where: columnField(whereLabel, (context) =>
            context.columns
                .filter((column) => column.type === 'boolean')
                .map((column) => column.key)
        ),
    };
}
